<script setup lang="ts">
import merge from 'lodash/merge';

interface FormSelectList
{
    content: {
        value: {
            description: string;
            questions: SingleSelect[];
        };
    };
}

interface SingleSelect
{
    id: number;
    question: string;
    answers: Answer[];
}

interface Answer
{
    id: number;
    text: string;
    isCorrect: boolean;
}

interface Props
{
    modelValue: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    'update:modelValue': [value: any];
}>();

const defaults: any = {
    description: '',
    questions: [] as SingleSelect[]
};

const form = computed((): FormType<FormSelectList> =>
{
    return props.modelValue;
});

onBeforeMount(() =>
{
    form.value.content.value = merge(defaults, form.value.content.value);
    form.value.file = null;

    if (!form.value.content.value.questions.length)
    {
        form.value.content.value.questions = [{
            id: 1,
            question: '',
            answers: [
                { id: 1, text: '', isCorrect: true }
            ]
        }];
    }

    form.value.content.value.questions.forEach((q) =>
    {
        if (!q.answers.length)
        {
            q.answers = [{ id: 1, text: '', isCorrect: true }];
        }
    });
});

function addQuestion(): void
{
    const ids = form.value.content.value.questions.map((x: { id: any }) => x.id);

    const maxId = form.value.content.value.questions.length > 0 ? Math.max(...ids) : 1;

    form.value.content.value.questions.push({
        id: maxId + 1,
        question: '',
        answers: [
            { id: 1, text: '', isCorrect: true }
        ]
    });
}

function removeQuestion(index: number): void
{
    const items = form.value.content.value.questions.slice();

    items.splice(index, 1);
    form.value.content.value.questions = items;

    form.value.$errors.clear();
}

function addAnswer(question: any): void
{
    const ids = question.answers.map((x: { id: any }) => x.id);

    const maxId = question.answers.length > 0 ? Math.max(...ids) : 1;

    question.answers.push({
        id: maxId + 1,
        text: '',
        isCorrect: false
    });
}

function removeAnswer(question: any, index: number): void
{
    question.answers.splice(index, 1);

    const questionIndex = form.value.content.value.questions.indexOf(question);

    form.value.$errors.clear(`content.value.questions[${questionIndex}].answers[${index}]`);
}

function isCorrectChanged(question: SingleSelect, index: number): void
{
    const clickedAnswer = question.answers[index];
    const clickedAnswerValue = clickedAnswer.isCorrect;

    const count = question.answers.filter((item) =>
    {
        if (item.id == clickedAnswer.id)
            return item.isCorrect;
    }).length;

    if (count == 1) return;

    question.answers[index].isCorrect = !clickedAnswerValue;

    for (const answer of question.answers)
    {
        if (answer.id != question.answers[index].id)
        {
            answer.isCorrect = false;
        }
    }

    const questionIndex = form.value.content.value.questions.indexOf(question);

    form.value.$errors.clear(`content.value.questions[${questionIndex}].answers`);
}

watch(() => form.value.content.value, (value: any) =>
{
    triggetModel();
}, { deep: true });

function triggetModel(): any
{
    emit('update:modelValue', form.value);
}

function moveUp(index: number, parentIndex?: number): void
{
    if (index > 0)
    {
        if (parentIndex != undefined)
        {
            const answers = form.value.content.value.questions[parentIndex].answers.splice(index, 1);

            form.value.content.value.questions[parentIndex].answers.splice(index - 1, 0, answers[0]);

            form.value.$errors.clear();
        }
        else
        {
            const answers = form.value.content.value.questions.splice(index, 1);

            form.value.content.value.questions.splice(index - 1, 0, answers[0]);

            form.value.$errors.clear();
        }
    }
}

function moveDown(index: number, parentIndex?: number): void
{
    if (parentIndex != undefined)
    {
        if (index < form.value.content.value.questions[parentIndex].answers.length - 1)
        {
            const answers = form.value.content.value.questions[parentIndex].answers.splice(index, 1);

            form.value.content.value.questions[parentIndex].answers.splice(index + 1, 0, answers[0]);

            form.value.$errors.clear();
        }
    }
    else
    {
        if (index < form.value.content.value.questions.length - 1)
        {
            const answers = form.value.content.value.questions.splice(index, 1);

            form.value.content.value.questions.splice(index + 1, 0, answers[0]);

            form.value.$errors.clear();
        }
    }
}
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Opis]]]')" :invalid-feedback="form.$errors.first('content.value.description')" :state="form.$errors.state('content.value.description')">
        <RichTextEditor
            :id="seconds('description-' + form.id)"
            v-model="form.content.value.description"
            name="description"
            shortcode
            timeline
        />
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Pytania i odpowiedzi]]]')" :invalid-feedback="form.$errors.first('content.value.questions')" :state="form.$errors.state('content.value.questions')" required>
        <div v-for="(question, i) in form.content.value.questions" :key="i" class="row g-0 mb-3">
            <div class="bg-light col-12 col-lg-11 p-2">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text">{{ i + 1 }}</span>
                    </div>
                    <IdeoFormInput v-model="question.question" type="text" :name="`content.value.questions[${i}].question`" :placeholder="$t('[[[Pytanie]]]')" :data-cy="`question-${i}`" />
                    <div class="input-group-append me-2">
                        <ActionButton
                            variant="danger"
                            icon="far fa-trash"
                            :title="$t('[[[Usuń pytanie]]]')"
                            :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                            :disabled="form.content.value.questions.length < 2"
                            @click="removeQuestion(i)"
                        />
                    </div>
                </div>
                <small v-if="!form.$errors.state(`content.value.questions[${i}].question`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].question`) }}</small>
                <hr>
                <div v-for="(answer, j) in question.answers" :key="j" class="row mb-2">
                    <div class="col-md-9 col-sm-7">
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text bg-primary border-primary text-white">{{ i + 1 }}.{{ j + 1 }}</span>
                            </div>
                            <IdeoFormTextarea v-model="answer.text" :name="`content.value.questions[${i}].answers[${j}]`" :rows="2" :placeholder="$t('[[[Odpowiedź]]]')" :data-cy="`answer-${i}-${j}`" />
                            <div class="input-group-append">
                                <ActionButton :variant="answer.isCorrect ? 'success' : 'secondary'" icon="far fa-check" :title="$t('[[[Poprawna odpowiedź]]]')" data-cy="isCorrect" @click="isCorrectChanged(question, j)" />
                                <ActionButton
                                    variant="danger"
                                    icon="far fa-trash"
                                    :title="$t('[[[Usuń odpowiedź]]]')"
                                    :confirm="$t('[[[Potwierdzenie usunięcia]]]')"
                                    :disabled="question.answers.length < 2"
                                    @click="removeAnswer(question, j)"
                                />
                            </div>
                        </div>
                        <small v-if="!form.$errors.state(`content.value.questions[${i}].answers[${j}]`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].answers[${j}]`) }}</small>
                    </div>
                    <div class="col-md-3 col-sm-5 mt-2 mt-sm-0 d-flex">
                        <ActionButton
                            variant="secondary"
                            icon="far fa-arrow-up"
                            class="ms-auto"
                            :title="$t('[[[Wyżej]]]') "
                            :disabled="j == 0"
                            @click="moveUp(j, i)"
                        />
                        <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="j == question.answers.length - 1" @click="moveDown(j, i)" />
                    </div>
                </div>
                <small v-if="!form.$errors.state(`content.value.questions[${i}].answers`)" class="form-text text-danger">{{ form.$errors.first(`content.value.questions[${i}].answers`) }}</small>
                <div class="d-flex mb-3">
                    <ActionButton
                        variant="primary"
                        class="mx-auto"
                        icon="far fa-plus"
                        :text="$t('[[[Dodaj odpowiedź]]]')"
                        :data-cy="`addAnswer-${i}`"
                        @click="addAnswer(question)"
                    />
                </div>
            </div>
            <div class="col-12 col-lg-1">
                <div class="ms-2">
                    <ActionButton
                        variant="secondary"
                        icon="far fa-arrow-up"
                        class="ms-auto"
                        :title="$t('[[[Wyżej]]]') "
                        :disabled="i == 0"
                        @click="moveUp(i)"
                    />
                    <ActionButton variant="secondary" icon="far fa-arrow-down" :title="$t('[[[Niżej]]]')" :disabled="i == form.content.value.questions.length - 1" @click="moveDown(i)" />
                </div>
            </div>
        </div>
        <div>
            <ActionButton
                variant="outline-dark"
                class="w-100"
                icon="far fa-plus"
                :text="$t('[[[Dodaj pytanie]]]')"
                data-cy="addQuestion"
                @click="addQuestion()"
            />
        </div>
    </IdeoFormGroup>
</template>
